/**
 * creativeDigital
 * ---------------
 * Handles product-image gallery behavior inside Creative Digital sections
 * (e.g. opening a full-screen modal with next/prev controls, thumbnail strip,
 * and optional collapsible sidebar menu).
 *
 * The constructor is called automatically by Shopify’s theme script runner,
 * which passes the **section** object that contains:
 *   • section.el    → The root DOM node of the section.
 *   • section.id    → Shopify’s autogenerated section ID.
 *   • section.data  → JSON settings coming from the Theme Editor.
 *
 */
class creativeDigital {
  /**
   * @param {Object} section – Section descriptor injected by Shopify.
   */
  constructor(section) {
    this.section = window.document;

    /** Root element of the section. */
    this.sectionEl = section.el;

    /** Will hold the Modal instance once it is created. */
    this.modal = null;

    /** Hard-coded fallback settings in case Theme Editor didn’t send any. */
    this.defaultSettings = {
      /** Whether the sidebar’s accordion panels should start expanded. */
      sideMenuTabsOpen: false,
      /** Where to place the thumbnail strip: 'left' | 'right' | 'bottom'. */
      thumbnailPosition: 'left',
      /** Size modifier for thumbnails: 'small' | 'medium' | 'large'. */
      thumbnailSize: 'small',
      key_point_class: '.dynamic-gallery--keypoints'
    };

    /** Final settings = data from Theme Editor OR fallback above. */
    this.settings = section.data.settings || this.defaultSettings;

    /** Raw section data (often contains product JSON, etc.). */
    this.sectionData = section.data;
    this.flickity_container = null
    /* ------------- DOM queries ------------- */
    /** All figure elements that have a `data-image` attribute. */
    this.galleryItems = Array.from(this.section.querySelectorAll('[data-image]'));

    /** CSS selector that points to the hidden Quick-Shop template. */
    this.gallerySelector = `#shopify-section-${section.id} [data-product-quickshop]`;

    /** jQuery wrapper around the selector – used by Modal script. */
    this.$gallerySelector = window.jQuery(this.gallerySelector);

    /** Optional sidebar wrapper (may not exist on all templates). */
    this.datasidebar = window.document.querySelector('[data-sidebar-wrapper]');

    /* ------------- Callbacks passed into the Modal library ------------- */
    this.modalCallbacks = {
      onOpen:  this._open.bind(this),      // Fires right AFTER modal HTML is inserted.
      onClose: this.closeModal.bind(this), // Fires right BEFORE modal is destroyed.
    };

    this._onResize = this._debounce(this._handleResize.bind(this), 150);

    window.addEventListener('resize', this._onResize, { passive: true });

    this._handleResize()

    this.handleArrows = this.handleArrows.bind(this);

    /* ------------- Event listeners ------------- */
    this.handleImageClick = this._onImageClick.bind(this);

    // Index every <figure> with its position and listen for clicks.
    this.galleryItems.forEach((item, idx) => {
      item.dataset.index = idx;
      item.addEventListener('click', this.handleImageClick);
    });

    /** Index of the image currently shown in the modal (null before open). */
    this.currentIndex = null;

    /* ------------- Kick-off ------------- */
    this.init();
  }

  /* --------------------------------------------------------------------
   * Public-ish inner methods
   * ------------------------------------------------------------------ */

  /**
   * Initializes optional UI parts (currently the collapsible sidebar).
   * Called automatically from the constructor.
   */
  init() {
    if (this.datasidebar) this.sideBar();
  }

  _debounce(fn, wait = 150) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  }

  _handleResize() {
    const container = this.sectionEl.querySelector(this.defaultSettings.key_point_class)
    const cards = Array.from(container.querySelectorAll(".dynamic-gallery--keypoint"))
    const maxW = this.sectionEl.getBoundingClientRect().width;
    let width = 0;
    
    if (cards.length) {
      cards.forEach(card => {
        let sum = card.getBoundingClientRect().width;
        width += sum;
      })
      if (width > maxW && !this.flickity_container) {
        this.flickity_container = this.setupFlickity({
            container: container,
            cellSelector: '.dynamic-gallery--keypoint',
            freeScroll: false,
            buttons: false,
            draggable: '>1',
          });

          this.flickity_container.resize()
      } 
      if (width < maxW && this.flickity_container) {
        this.flickity_container.destroy()
      }
    }
  }

  /**
   * Adds accordion behavior to the sidebar menu.
   */
  sideBar() {
    /** All collapsible sections inside the sidebar. */
    const sections = Array.from(this.datasidebar.querySelectorAll('.section'));

    sections.forEach((section) => {
      const button        = section.querySelector('.section-header-sidebar'); // accordion header
      const listContainer = section.querySelector('.case-studies-list');      // accordion body

      // Theme-editor setting: should every accordion be open on load?
      if (this.settings.sideMenuTabsOpen) {
        button.classList.add('open');
        listContainer.dataset.open = 'true';
      }

      // Toggle open/close on click
      button.addEventListener('click', () => {
        this.openContainer(button, listContainer);
      });
    });
  }

  /**
   * Toggles a single accordion container.
   * Adds a tiny 100 ms delay when OPENING to avoid two height animations
   * (button rotation + list slide) running at the exact same frame.
   *
   * @param {HTMLElement} button        – The header element.
   * @param {HTMLElement} listContainer – The body element.
   */
  openContainer(button, listContainer) {
    const isOpen     = button.classList.contains('open');
    const listIsOpen = listContainer.dataset.open === 'true';

    // Close state
    if (isOpen && listIsOpen) {
      button.classList.remove('open');
      listContainer.dataset.open = 'false';
    }
    // Open state (delay to reduce jank)
    else {
      setTimeout(() => {
        button.classList.add('open');
        listContainer.dataset.open = 'true';
      }, 100);
    }
  }

  /* --------------------------------------------------------------------
   * Gallery & Modal logic
   * ------------------------------------------------------------------ */

  /**
   * Click handler for all thumbnail <figure> elements in the product page.
   * Opens the modal gallery on desktop only.
   *
   * @param {MouseEvent} e
   */
  _onImageClick(e) {
    e.preventDefault();
    e.stopPropagation();

    // Abort on mobile: gallery is designed only for viewport > 740 px.

    const idx = Number(e.currentTarget.dataset.index);
    this.initGallery(idx);
  }

  /**
   * Creates the modal the first time it is needed and injects navigation
   * buttons, high-resolution image container, and a thumbnail strip.
   *
   * @param {number} startIndex – Zero-based index of the image to show first.
   */
  initGallery(startIndex) {
    /* 1. Create or reopen the Modal ----------------------------- */
    if (this.modal === null) {
      this.modal = new window.Modal(this.modalCallbacks);
      this.modal.open(this.gallerySelector, 'gallery-modal');
    }

    /** Reference to the inner element inside the modal where we can append nodes. */
    this.$quickShopModalContent = this.modal.$modalInner.find('[data-modal-content]');
    this.modal.$modal[0].classList.add("creative-design")

    /* 2. Build navigation buttons ------------------------------ */
    this.nextBtn = document.createElement('button');
    this.leftBtn = document.createElement('button');
    this.nextBtn.classList.add('gallery-modal__next');
    this.leftBtn.classList.add('gallery-modal__prev');
    this.nextBtn.setAttribute('aria-label', 'Next image');
    this.leftBtn.setAttribute('aria-label', 'Previous image');
    this.nextBtn.innerHTML =
      `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
            viewBox="0 0 20 24" fill="none" stroke="currentColor"
            stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
            class="feather feather-chevron-right">
         <polyline points="9 18 15 12 9 6"></polyline>
       </svg>`;
    this.leftBtn.innerHTML =
      `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
            viewBox="0 0 26 24" fill="none" stroke="currentColor"
            stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
            class="feather feather-chevron-left">
         <polyline points="15 18 9 12 15 6"></polyline>
       </svg>`;

    /* 3. Build main containers -------------------------------- */
    this.containerGallery = document.createElement('div');
    this.containerGallery.classList.add('gallery-modal__container');

    this.imageWrapper = document.createElement('div');
    this.imageWrapper.classList.add('gallery-modal__image-wrapper');

    /* 4. Build the thumbnail strip ---------------------------- */
    this.galleryThumbNails = document.createElement('div');
    this.galleryThumbNails.classList.add('gallery-modal__thumbs');
    this.galleryThumbNails.dataset.position = this.settings.thumbnailPosition;

    /* 5. Assemble DOM ----------------------------------------- */
    this.containerGallery.appendChild(this.leftBtn);
    this.containerGallery.appendChild(this.imageWrapper);
    this.containerGallery.appendChild(this.nextBtn);

    // Add positional class to modal inner (for CSS flex/grid layouts).
    this.$quickShopModalContent[0]
      .classList
      .add(`gallery-modal__${this.settings.thumbnailPosition}`);

    this.$quickShopModalContent.append(this.containerGallery);
    this.$quickShopModalContent.append(this.galleryThumbNails);

    /* 6. Populate thumbnail strip ----------------------------- */
    this.galleryItems.forEach((item, index) => {
      const thumbFigure = item.cloneNode(true);     // duplicate <figure>
      const img = thumbFigure.querySelector("img")
      thumbFigure.classList.add('gallery-modal__thumb');
      thumbFigure.setAttribute('data-thumb-index', index);
      if (img) this._revealImage(img);
      thumbFigure.addEventListener('click', () => {
        this.changeImage(index, null);
      });

      this.galleryThumbNails.appendChild(thumbFigure);
    });

    /* 7. Wire navigation buttons ------------------------------ */
    this.leftBtn.addEventListener('click', () => {
      const prevIndex = (this.currentIndex - 1 + this.galleryItems.length) %
                        this.galleryItems.length;
      this.changeImage(prevIndex, 'fade-right');
    });


    this.nextBtn.addEventListener('click', () => {
      const nextIndex = (this.currentIndex + 1) % this.galleryItems.length;
      this.changeImage(nextIndex, 'fade-left');
    });

    /* 8. Show the requested starting image -------------------- */
    this.changeImage(startIndex, null);

    document.addEventListener('keydown', this.handleArrows);

    if (window.innerWidth <= 768) {
      this.modal.$modal[0].classList.add("creative-mobile")

       if (!this.flickity) {
        this.flickity = this.setupFlickity({
          container: this.galleryThumbNails,
          cellSelector: '[data-image]',
          freeScroll: false,
          buttons: false,
          draggable: '>1',
          groupCells: true
        });

        setTimeout(() => {
          if (this.flickity) {
            this.flickity.resize();
          }
        }, 100);
      }
    };
  }

  _revealImage(img) {
        const dataSrc = img.getAttribute('data-src');
        const dataSrcset = img.getAttribute('data-srcset');
        if (dataSrc && !img.getAttribute('src')) img.setAttribute('src', dataSrc);
        if (dataSrcset && !img.getAttribute('srcset')) img.setAttribute('srcset', dataSrcset);
        img.classList.remove('lazyHide', 'lazyload', 'rimg__lazy');
        // Native loading="lazy" fallback for when the app isn't active.
        if (img.loading === 'lazy') img.loading = 'eager';
  }

  handleArrows(event) {
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      const nextIndex = (this.currentIndex + 1) % this.galleryItems.length;
      this.changeImage(nextIndex, 'fade-left');
      this.nextBtn.active()
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        const prevIndex = (this.currentIndex - 1 + this.galleryItems.length) %
                        this.galleryItems.length;
      this.changeImage(prevIndex, 'fade-right');
      this.leftBtn.active()
    }
  }

  /**
   * Replaces the high-resolution picture inside the modal and updates the
   * active thumbnail. Adds a small CSS animation if requested.
   *
   * @param {number} index      – Index of the galleryItems array.
   * @param {string|null} animation – Optional CSS class to fade left/right.
   */
  changeImage(index, animation) {
    const figure = this.galleryItems[index];

    // Pull the HD source from custom data-attribute.
    const fullSrc = figure.getAttribute('data-full');
    const imgAlt  = figure.querySelector('img')?.alt || '';

    const highResImage = document.createElement('img');
    highResImage.src  = fullSrc;
    highResImage.alt  = imgAlt;
    highResImage.classList.add('gallery-modal__main-img');
    if (animation) highResImage.classList.add(animation);

    // Swap current image.
    this.imageWrapper.innerHTML = '';
    this.imageWrapper.appendChild(highResImage);
    this.currentIndex = index;

    // Highlight active thumbnail.
    this.galleryThumbNails
      .querySelectorAll('.gallery-modal__thumb')
      .forEach((el) => el.classList.remove('active-thumb'));

    const activeThumb = this.galleryThumbNails
      .querySelector(`[data-thumb-index="${index}"]`);
    if (activeThumb) activeThumb.classList.add('active-thumb');

    // Remove animation class after it finishes.
    if (animation) {
      setTimeout(() => highResImage.classList.remove(animation), 500);
    }

    if (this.flickity) {
      this.flickity.select(index)
    }
  }

  /**
   * Cleans up DOM nodes and event listeners when the modal is closed,
   * then reinstates click listeners on the inline thumbnails so the user
   * can reopen the gallery.
   */
  closeModal() {
    // Remove nodes injected into the modal.
    this.modal.$modal[0].classList.remove("creative-design")
    if (window.innerWidth <= 768) {
      this.modal.$modal[0].classList.remove("creative-mobile")
    };
    this.containerGallery.remove();
    this.galleryThumbNails.remove();
    this.nextBtn.remove();
    this.leftBtn.remove();
    this.imageWrapper.remove();
    this.$quickShopModalContent[0]
      .classList
      .remove(`gallery-modal__${this.settings.thumbnailPosition}`);

    // Clear original hidden quick-shop HTML (avoids duplicate IDs).
    this.$gallerySelector.empty();
    this.$gallerySelector.html(this.quickshopSpinner);

    // Fully destroy the Modal instance.
    this.modal.unload();
    this.modal = null;

    // Remove old click listeners from thumbnails.
    this.galleryItems.forEach((item) => {
      item.removeEventListener('click', this.handleImageClick);
    });

    // Re-attach listeners so the gallery can be opened again.
    this.newListeners();
    document.removeEventListener('keydown', this.handleArrows);
  }

  setupFlickity({ container, cellSelector, arrowShape, buttons, freeScroll, ...settings }) {
    if (!container) return null;
    return new (window.js_default())(container, {
      accessibility: false,
      adaptiveHeight: false,
      cellSelector: cellSelector ?? null,
      wrapAround: false,
      contain: true,
      margin: 30,
      cellAlign: 'center',
      friction: 0.8,
      freeScroll: !!freeScroll,
      initialIndex: 0,
      lazyLoad: false,
      percentPosition: false,
      prevNextButtons: !!buttons,
      arrowShape: arrowShape || null,
      pageDots: false,
      ...settings
    });
  }

  /**
   * Re-indexes galleryItems and binds the click handler again.
   * Called after the modal has been torn down.
   */
  newListeners() {
    this.galleryItems.forEach((item, idx) => {
      item.dataset.index = idx;
      item.addEventListener('click', this.handleImageClick);
    });
    this.currentIndex = null;
  }

  /**
   * Empty hook fired by the Modal script immediately after opening.
   * You can add custom logic here if needed (e.g. focus trapping).
   */
  _open() { /* intentionally left blank */ }
}

document.addEventListener('DOMContentLoaded', () => {
  const section = new window.Sections()

  section.register('creative-digital-signage', section => new creativeDigital(section));
});
